import { useId } from "react";
import { TriangleAlert } from "lucide-react";
import { en, type Translations } from "../../i18n/en";
import { useLanguage } from "../../i18n/language-provider";
import { pluralForm } from "../../i18n/plural";
import { pmsDisplayName } from "../drafts/pmsDisplayName";

interface MinStayPerNightNoticeProps {
    /** The PMS named in the notice. */
    providerType: string;
    /**
     * Whether one minimum stay per night is the hotel's own setting in the
     * PMS, which could keep one per room type as well: the PMS was asked how
     * this hotel keeps them (`MinStayScopeStatus.read`). The notice says the
     * PMS can only there. A PMS that keeps one per night for every hotel has
     * no such setting, and is not said to. Required, so that no caller names
     * a setting its PMS does not have.
     */
    hotelSetting: boolean;
    /**
     * How many nights of the send on screen have room types asking for
     * different minimum stays — the nights the send levels. Left out where
     * nothing is being sent yet (the auto-sync dialog).
     */
    levelledNights?: number;
    /**
     * `"send"` beside the minimum-stay switch of the Send and auto-sync
     * dialogs; `"roomKinds"` under the room types' own minimum stays in Rate
     * Restrictions, where no send is on screen and the notice says what a room
     * type's own minimum becomes in the PMS instead.
     */
    variant?: "send" | "roomKinds";
}

/** The notice's words, defaulted field by field: a partial `t` is what some harnesses hand components. */
function minStayScopeCopy(t: Translations | { minStayScope?: Partial<Translations["minStayScope"]> }) {
    return { ...en.minStayScope, ...((t as { minStayScope?: Partial<Translations["minStayScope"]> }).minStayScope ?? {}) };
}

/**
 * Said beside the minimum-stay switch of the Send dialog and the auto-sync
 * dialog where the PMS keeps one minimum stay per night for every room type —
 * a Previo hotel kept per season. Optivo plans a minimum stay per room type, so
 * a send there levels each night to the highest of its room types, and the
 * operator has to know that before switching minimum stays on: Hotel Ostrov's
 * first minimum-stay sync, before the levelling, left Sat 10 Oct 2026 at
 * whichever room type was written last.
 *
 * It states what Optivo does and asks nothing of the operator, in this order:
 * nothing needs to be changed in the PMS and what is sent, how many nights of
 * the send on screen that changes, what is lost by it, and last that the PMS
 * can keep one per room type. It used to end on "To keep a minimum stay for
 * each room type, set {pms} to keep minimum stays per room type". Hotel Zámek
 * Svijany read that as a demand it believed its PMS could not meet, and did not
 * save (OPT-55, 9 Oct 2026). No sentence here tells anybody to set anything.
 */
export function MinStayPerNightNotice({ providerType, hotelSetting, levelledNights, variant = "send" }: MinStayPerNightNoticeProps) {
    const { t, locale } = useLanguage();
    const copy = minStayScopeCopy(t);
    const pms = pmsDisplayName(t, providerType);
    const titleId = useId();

    return (
        <div
            role="note"
            aria-labelledby={titleId}
            className="mt-2 space-y-1 rounded-md border border-dashed border-warning/60 bg-warning-surface p-3"
        >
            <p id={titleId} className="flex items-center gap-2 text-sm font-medium text-warning">
                <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
                {copy.title.replaceAll("{pms}", pms)}
            </p>
            {variant === "roomKinds" ? (
                <p className="text-xs text-foreground">{copy.roomKindsBody.replaceAll("{pms}", pms)}</p>
            ) : (
                <>
                    <p className="text-xs text-foreground">{copy.body.replaceAll("{pms}", pms)}</p>
                    {/* Straight after the sentence that says what is sent: its "that"
                        is the levelling. */}
                    {levelledNights !== undefined && levelledNights > 0 && (
                        <p className="text-xs text-foreground">{pluralForm(locale, levelledNights, copy.nights)}</p>
                    )}
                    <p className="text-xs text-foreground">{copy.lost.replaceAll("{pms}", pms)}</p>
                </>
            )}
            {hotelSetting && (
                <p className="text-xs text-muted-foreground">{copy.option.replaceAll("{pms}", pms)}</p>
            )}
        </div>
    );
}
